import { UploadButton } from '@bytescale/upload-widget-react';
import { Button, Input, Select, SelectItem } from '@heroui/react';
import React, { FC } from 'react';
import { useForm } from 'react-hook-form';

import {
  ActionBlock,
  FileCard,
  Flex,
  FormErrorGroup,
  Grid,
  inputProps,
} from '../../../../components';
import { AD_TYPE_LABELS, AD_TYPES, AdRequest } from '../../../../queries';
import { HotelPicker } from '../HotelPicker';
import { AdFormEntity, AdFormOptions } from './AdForm.schema';
import { optionAdImageUpload } from './config';

interface AdFormProps {
  onSubmit: (data: AdRequest) => void;
  formDataId: string;
  defaultValues?: Partial<AdFormEntity>;
  /** Na edycji przypisania hoteli zapisuje osobny endpoint, więc formularz ich nie pokazuje. */
  withHotels?: boolean;
}

const EMPTY_FORM: AdFormEntity = {
  name: '',
  image: '',
  type: 'BANNER',
  order: 0,
  url: '',
  assigned_hotels: [],
};

export const AdForm: FC<AdFormProps> = ({
  onSubmit,
  formDataId,
  defaultValues,
  withHotels = false,
}) => {
  // Wartości startowe czytamy TYLKO przy montowaniu — strona edycji trzyma
  // formularz za spinnerem, dopóki nie ma dokumentu. Gdyby formularz nadążał
  // za każdą zmianą danych, dopięcie hotelu (osobny zapis, odświeża katalog)
  // kasowałoby niezapisane poprawki w treści reklamy.
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState,
  } = useForm<AdFormEntity>({
    ...AdFormOptions,
    defaultValues: { ...EMPTY_FORM, ...defaultValues },
  });

  const image = watch('image');
  const type = watch('type');
  const assignedHotels = watch('assigned_hotels');

  if (!optionAdImageUpload.apiKey || !optionAdImageUpload.path) {
    return <div>Brak konfiguracji Bytescale (klucz API lub bucket).</div>;
  }

  const submit = (data: AdFormEntity): void => {
    onSubmit({
      name: data.name,
      image: data.image,
      type: data.type,
      order: data.order,
      url: data.url,
      ...(withHotels ? { assigned_hotels: data.assigned_hotels } : {}),
    });
  };

  return (
    <>
      <FormErrorGroup errors={formState.errors} />
      <form noValidate onSubmit={handleSubmit(submit)} data-testid={formDataId} id={formDataId}>
        <Grid gridTemplateColumns="6fr 3fr" justifyContent="start" itemAlign="start" gap="25px">
          <div>
            <ActionBlock
              title="Reklama"
              description="Nazwa jest tylko dla nas — do hoteli schodzi wyłącznie grafika, typ, kolejność i link."
            >
              <Flex direction="column" gap="20px" margin="20px 0">
                <Input
                  isRequired
                  {...inputProps}
                  {...register('name')}
                  label="Nazwa"
                  placeholder="np. Kampania letnia — Żywiec"
                  type="text"
                  isInvalid={!!formState.errors.name?.message}
                  errorMessage={formState.errors.name?.message}
                />

                <Select
                  size="lg"
                  radius="sm"
                  variant="bordered"
                  labelPlacement="outside"
                  label="Typ"
                  selectedKeys={new Set([type])}
                  onSelectionChange={(keys) => {
                    const [next] = Array.from(keys as Set<string>);
                    if (next) setValue('type', next as AdFormEntity['type']);
                  }}
                  isInvalid={!!formState.errors.type?.message}
                  errorMessage={formState.errors.type?.message}
                >
                  {AD_TYPES.map((adType) => (
                    <SelectItem key={adType} textValue={AD_TYPE_LABELS[adType]}>
                      {AD_TYPE_LABELS[adType]}
                    </SelectItem>
                  ))}
                </Select>

                <Input
                  {...inputProps}
                  {...register('order')}
                  label="Kolejność"
                  placeholder="0"
                  type="number"
                  min={0}
                  description="Rosnąco — mniejsza liczba wyświetla się wyżej."
                  isInvalid={!!formState.errors.order?.message}
                  errorMessage={formState.errors.order?.message}
                />

                <Input
                  {...inputProps}
                  {...register('url')}
                  label="Link po kliknięciu"
                  placeholder="https://..."
                  type="url"
                  description="Puste = reklama bez akcji."
                  isInvalid={!!formState.errors.url?.message}
                  errorMessage={formState.errors.url?.message}
                />
              </Flex>
            </ActionBlock>

            {withHotels && (
              <ActionBlock
                title="Hotele"
                description="Reklama powstanie w kolekcji `ads` każdego zaznaczonego hotelu — pod tym samym id dokumentu."
              >
                <HotelPicker
                  value={assignedHotels}
                  onChange={(hotelIds) => setValue('assigned_hotels', hotelIds)}
                />
              </ActionBlock>
            )}
          </div>

          <div>
            <ActionBlock title="Grafika" description="Jeden obrazek, maks. 4 MB.">
              {image === '' && (
                <UploadButton
                  options={optionAdImageUpload}
                  onComplete={(files) => setValue('image', files[0]?.fileUrl || '', {
                    shouldValidate: true,
                  })}
                >
                  {({ onClick }) => (
                    <Button onClick={onClick} color="secondary" size="lg">
                      Wgraj grafikę...
                    </Button>
                  )}
                </UploadButton>
              )}
              {image !== '' && (
                <FileCard
                  fileName={image}
                  fileType="IMAGE"
                  onRemove={() => setValue('image', '')}
                />
              )}
              {formState.errors.image?.message && (
                <p className="text-sm text-danger mt-2">{formState.errors.image.message}</p>
              )}
            </ActionBlock>
          </div>
        </Grid>
      </form>
    </>
  );
};
